/**
 * Validação e formatação de documentos brasileiros (CNPJ, CPF, CEP).
 * O banco grava só os dígitos; a tela mostra formatado. A mesma regra do CNPJ
 * existe no banco (privado.cnpj_valido), que é quem garante a integridade.
 */

/** Remove tudo que não for dígito. */
export function soDigitos(valor: string | null | undefined): string {
  return (valor ?? "").replace(/\D/g, "");
}

function digitoVerificador(digitos: string, pesos: number[]): number {
  const soma = pesos.reduce((total, peso, i) => total + Number(digitos[i]) * peso, 0);
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/** CNPJ válido pelos dígitos verificadores. Aceita com ou sem pontuação. */
export function cnpjValido(valor: string | null | undefined): boolean {
  const cnpj = soDigitos(valor);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
  const dv1 = digitoVerificador(cnpj, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const dv2 = digitoVerificador(cnpj, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return Number(cnpj[12]) === dv1 && Number(cnpj[13]) === dv2;
}

/** CPF válido pelos dígitos verificadores. Aceita com ou sem pontuação. */
export function cpfValido(valor: string | null | undefined): boolean {
  const cpf = soDigitos(valor);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const calcular = (n: number) => {
    const soma = Array.from({ length: n }, (_, i) => Number(cpf[i]) * (n + 1 - i)).reduce((a, b) => a + b, 0);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  return Number(cpf[9]) === calcular(9) && Number(cpf[10]) === calcular(10);
}

/** "50833829000143" → "50.833.829/0001-43". Valor incompleto volta como está. */
export function formatarCnpj(valor: string | null | undefined): string {
  const d = soDigitos(valor);
  return d.length === 14 ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : (valor ?? "");
}

/** "12345678909" → "123.456.789-09". */
export function formatarCpf(valor: string | null | undefined): string {
  const d = soDigitos(valor);
  return d.length === 11 ? d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4") : (valor ?? "");
}

/** "49020490" → "49.020-490" (formato do Cartão CNPJ). */
export function formatarCep(valor: string | null | undefined): string {
  const d = soDigitos(valor);
  return d.length === 8 ? d.replace(/^(\d{2})(\d{3})(\d{3})$/, "$1.$2-$3") : (valor ?? "");
}

/**
 * Código de barras GTIN (EAN-8, UPC-A/GTIN-12, EAN-13, GTIN-14/DUN-14): dígito verificador módulo 10 (GS1).
 * Códigos internos (outros tamanhos ou com letras) não são GTIN e retornam false.
 */
export function gtinValido(valor: string | null | undefined): boolean {
  const d = (valor ?? "").trim();
  if (!/^\d+$/.test(d) || ![8, 12, 13, 14].includes(d.length)) return false;
  const corpo = d.slice(0, -1);
  let soma = 0;
  for (let i = 0; i < corpo.length; i++) {
    // Da direita para a esquerda, pesos 3, 1, 3, 1…
    const digito = Number(corpo[corpo.length - 1 - i]);
    soma += digito * (i % 2 === 0 ? 3 : 1);
  }
  const verificador = (10 - (soma % 10)) % 10;
  return verificador === Number(d[d.length - 1]);
}

/** NCM com 8 dígitos: "84716052" → "8471.60.52". */
export function formatarNcm(valor: string | null | undefined): string {
  const d = soDigitos(valor);
  return d.length === 8 ? d.replace(/^(\d{4})(\d{2})(\d{2})$/, "$1.$2.$3") : (valor ?? "");
}
